import { useController } from "react-hook-form";

import { cn } from "@/lib/utils";

import Select, { SelectOption } from "../../inputs/Select/Select";
import { useFieldIds } from "../../useFieldIds";
import FieldError from "../FieldError/FieldError";
import FieldHint from "../FieldHint/FieldHint";
import FieldLabel from "../FieldLabel/FieldLabel";

type SelectFieldProps = {
  readonly name: string;
  readonly placeholder?: string;
  readonly label?: string;
  readonly options: SelectOption[];
  readonly direction?: "up" | "down";
  readonly hint?: string;
  readonly disabled?: boolean;
  readonly showAddButton?: boolean;
  readonly addButtonText?: string;
  readonly hideLabel?: boolean;
  readonly hideValidation?: boolean;
  readonly onChange?: (option: SelectOption | null) => void;
  readonly onAddNewOption?: () => void;
  /** If true, the field will only store the value of the selected option. onChange will still receive the full option object. */
  readonly valueOnly?: boolean;
  readonly variant?: "default" | "v3";
};

export default function SelectField({
  name,
  placeholder,
  label,
  options = [],
  direction,
  hint,
  showAddButton = false,
  addButtonText = "Add New",
  hideLabel = false,
  hideValidation = false,
  disabled = false,
  onChange,
  onAddNewOption,
  valueOnly = false,
  variant = "default",
}: SelectFieldProps): React.ReactElement {
  const { inputId, labelId, hintId, errorId, describedBy } = useFieldIds(name);

  const { field, fieldState } = useController({
    name,
  });

  const invalid = !!fieldState.error;

  const onSelect = (selected: SelectOption | null) => {
    if (valueOnly) {
      field.onChange(selected?.value ?? null);
    } else {
      field.onChange(selected);
    }
    onChange?.(selected);
  };

  const currentValue =
    typeof field.value === "object"
      ? (field.value as SelectOption).value
      : (field.value as unknown);

  const selectedOption = options.find((opt) => opt.value === currentValue);

  return (
    <div
      className={cn(
        "SelectField",
        "grid gap-2",
        disabled && "pointer-events-none opacity-50"
      )}
      data-testid="SelectField"
    >
      <div className={cn("grid", hideLabel && "sr-only")}>
        <FieldLabel
          htmlFor={inputId}
          id={labelId}
          invalid={invalid}
          value={label as string}
        />
      </div>

      <Select
        addButtonText={addButtonText}
        aria-describedby={describedBy(!!hint, !!fieldState.error)}
        aria-invalid={invalid}
        direction={direction}
        disabled={disabled}
        id={inputId}
        options={options}
        placeholder={placeholder}
        showAddButton={showAddButton}
        value={selectedOption}
        onAddNewOption={onAddNewOption}
        onSelect={onSelect}
        variant={variant}
      />

      <div
        className={cn(
          "align-center flex h-3 justify-start",
          hideValidation && "hidden"
        )}
      >
        {hint && !fieldState.error && <FieldHint hint={hint} id={hintId} />}
        {fieldState.error && (
          <FieldError error={fieldState.error} id={errorId} />
        )}
      </div>
    </div>
  );
}
